import React, { useEffect, useRef, useState } from 'react';
import { Button, InputAdornment, Icon, Typography, Select, MenuItem, FormControl, InputLabel, DialogTitle, DialogContent } from '@material-ui/core';
import { TextFieldFormsy } from '@fuse';
import Formsy from 'formsy-react';
import * as authActions from 'app/auth/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import Error from 'app/fuse-layouts/shared-components/Error';
import * as ActionsDialog from 'app/store/actions';

// Blocking error codes that should show a non-dismissable modal
const BLOCKING_ERROR_CODES = ['ORGANIZATION_SUSPENDED', 'LICENSE_EXPIRED', 'ORGANIZATION_NOT_FOUND'];

// Blocking error dialog component (no close button, cannot be dismissed)
const BlockingErrorDialog = ({ title, message }) => (
	<>
		<DialogTitle>
			<Typography className="font-bold text-red">{title}</Typography>
		</DialogTitle>
		<DialogContent>
			<Typography>{message}</Typography>
		</DialogContent>
	</>
);

const LdapLoginForm = (props) => {
	const dispatch = useDispatch();
	const formRef = useRef(null);

	const error = useSelector(({ auth }) => auth.auth.error);
	const licenseType = useSelector(({ licenseKey }) => licenseKey.key.licenseType);
	const activeLdapServers = useSelector(({ auth }) => auth.auth.activeLdapServers);

	const [isFormValid, setIsFormValid] = useState(false);
	const [changed, setChanged] = useState(false);
	const [selectedServer, setSelectedServer] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const isLdapAvailable = activeLdapServers && activeLdapServers.length > 0;

	useEffect(() => {
		if (error) {
			if (formRef.current && error.type === 'form') {
				let message = error.message;
				if (error === 'Unauthorized') message = error;
				const location = { username: message, password: message };
				formRef.current.updateInputsWithError({
					...location
				});
				disableButton();
			} else if (error.type === 'modal' && error.message) {
				// Determine title based on error code
				const blockingCodes = ['ORGANIZATION_SUSPENDED', 'LICENSE_EXPIRED', 'ORGANIZATION_NOT_FOUND'];
				const isBlockingError = blockingCodes.includes(error.code);
				const title = isBlockingError ? 'Access Denied' : undefined;

				dispatch(
					ActionsDialog.openDialog({
						children: <Error message={error.message} title={title} clearError={() => dispatch(authActions.clearError())} />,
						maxWidth: 'xs',
						fullWidth: true,
						onClose: (event, reason) => {
							if (reason !== 'backdropClick') {
								dispatch(ActionsDialog.closeDialog());
							}
						}
					})
				);
			}
		}
		dispatch(authActions.getActiveLdapServers());
	}, [dispatch, error, licenseType]);

	useEffect(() => {
		dispatch(authActions.getActiveLdapServers());
	}, [dispatch]);

	useEffect(() => {
		if (error && !changed) {
			dispatch(authActions.clearError());
		}
	}, [dispatch, error, changed]);

	useEffect(() => {
		if (isLdapAvailable && !selectedServer) {
			setSelectedServer(activeLdapServers[0].id);
		}
	}, [activeLdapServers, selectedServer]);

	const disableButton = () => setIsFormValid(false);

	const enableButton = () => setIsFormValid(true);

	const handleSubmit = model => {
		const data = {
			...model,
			username: model.username.toLowerCase(),
			ldapServerId: selectedServer
		};
		disableButton();
		setChanged(false);
		dispatch(authActions.submitLdapLogin(data));
	};
	if (!isLdapAvailable) {
		return (
			<div className="w-full">
				<Typography variant="h6" className="text-center md:w-full mb-48">
					No LDAP Servers Available
				</Typography>
				<Typography className="text-center mb-16">
					Contact your administrator to configure LDAP authentication.
				</Typography>
				<Button
					type="button"
					variant="outlined"
					color="secondary"
					className="w-full mx-auto mt-8"
					onClick={() => props.setSelectedLoginForm('local')}
				>
					Go Back
				</Button>
			</div>
		);
	}

	return (
		<div className="w-full">
			<Typography variant="h6" className="text-center md:w-full mb-48">
				Sign in with LDAP
			</Typography>
			<Formsy
				onValidSubmit={handleSubmit}
				onValid={enableButton}
				onInvalid={disableButton}
				onChange={() => setChanged(true)}
				ref={formRef}
				className="flex flex-col justify-center w-full"
			>
				<FormControl variant="outlined" className="mb-16">
					<InputLabel id="ldap-server-select-label">LDAP Server</InputLabel>
					<Select
						labelId="ldap-server-select-label"
						id="ldap_server_select"
						value={selectedServer}
						onChange={(e) => setSelectedServer(e.target.value)}
						label="LDAP Server"
						required
					>
						{activeLdapServers.map((server) => (
							<MenuItem key={server.id} value={server.id}>
								{server.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<TextFieldFormsy
					className="mb-16"
					type="text"
					name="username"
					id="ldap_login_form_username"
					label="Username"
					value=""
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									email
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<TextFieldFormsy
					className="mb-16"
					type={showPassword ? "text" : "password"}
					name="password"
					id="ldap_login_form_password"
					label="Password"
					value=""
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon 
									className="text-20 cursor-pointer" 
									color="action"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? 'visibility_off' : 'visibility'}
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<Button
					type="submit"
					variant="contained"
					id="login_form_submit"
					color="primary"
					className="w-full mx-auto mt-16"
					aria-label="Sign in"
					disabled={!isFormValid}
					value="legacy"
				>
					Sign in
				</Button>
			</Formsy>
			<Button
				type="button"
				variant="outlined"
				color="secondary"
				className="w-full mx-auto mt-8"
				onClick={() => props.setSelectedLoginForm('local')}
			>
				Go Back
			</Button>
		</div>
	);
};

export default LdapLoginForm;
